import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PictureOutlined,
  PlusOutlined,
  SearchOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Descriptions,
  Divider,
  Empty,
  Flex,
  Image,
  Input,
  List,
  message,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import service from "../../services/specstudioService";
import { specstudioPermissions } from "../../utils/permissions";
import ProductFormModal from "./components/admin/ProductFormModal";
import ProviderFormModal from "./components/admin/ProviderFormModal";
import SpecFormModal from "./components/admin/SpecFormModal";
import ProductCard from "./components/ProductCard";
import RequirementTable from "./components/RequirementTable";
import SkuGeneratorModal from "./components/SkuGeneratorModal";
import "./Specstudio.css";
const { Title, Text } = Typography;

// The library view mode is a per-viewer preference
const VIEW_MODE_KEY = "specstudio.viewMode";
const readViewMode = () => {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === "grid" ? "grid" : "table";
  } catch {
    return "table";
  }
};

export default function Specstudio() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const perms = useMemo(
    () => ({
      create: specstudioPermissions.canCreate(user),
      update: specstudioPermissions.canUpdate(user),
      delete: specstudioPermissions.canDelete(user),
    }),
    [user],
  );
  const [categories, setCategories] = useState([]),
    [products, setProducts] = useState([]),
    [pagination, setPagination] = useState({
      current: 1,
      pageSize: 24,
      total: 0,
    }),
    [product, setProduct] = useState(null),
    [loading, setLoading] = useState(true),
    [q, setQ] = useState(""),
    [query, setQuery] = useState(""),
    [categoryId, setCategoryId] = useState(null),
    [status, setStatus] = useState(""),
    [viewMode, setViewMode] = useState(readViewMode),
    [skuOpen, setSkuOpen] = useState(false),
    [modal, setModal] = useState(null),
    [actionLoadingId, setActionLoadingId] = useState(null),
    [activeProvider, setActiveProvider] = useState(null);
  const productListRequest = useRef(0);
  const loadCategories = useCallback(async () => {
    try {
      setCategories(await service.getCategories());
    } catch (e) {
      message.error(e.response?.data?.message || e.message);
    }
  }, []);
  const loadProducts = useCallback(async () => {
    const requestId = ++productListRequest.current;
    setLoading(true);
    try {
      const r = await service.getProducts({
        q: query,
        categoryId: categoryId || undefined,
        status,
        page: pagination.current,
        limit: pagination.pageSize,
      });
      if (requestId !== productListRequest.current) return;
      const items = r.items || [];
      const total = r.total || 0;
      const lastPage = Math.max(1, Math.ceil(total / pagination.pageSize));
      if (items.length === 0 && pagination.current > lastPage) {
        setPagination((current) => ({ ...current, current: lastPage, total }));
        return;
      }
      setProducts(items);
      setPagination((current) => ({
        ...current,
        current: r.page || pagination.current,
        total,
      }));
    } catch (e) {
      if (requestId !== productListRequest.current) return;
      message.error(e.response?.data?.message || e.message);
    } finally {
      if (requestId === productListRequest.current) setLoading(false);
    }
  }, [query, categoryId, status, pagination.current, pagination.pageSize]);
  const loadProduct = useCallback(async () => {
    if (!productId) {
      setProduct(null);
      return;
    }
    setLoading(true);
    try {
      const p = await service.getProductById(productId);
      setProduct(p);
      setActiveProvider(p.providers?.[0]?.id || null);
    } catch (e) {
      message.error(e.response?.data?.message || e.message);
      navigate("/optimization/specstudio");
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);
  // Search runs on Enter (or when the box is cleared), not while typing
  const applySearch = (value) => {
    setQuery(value.trim());
    setPagination((current) => ({ ...current, current: 1 }));
  };
  const changeViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // Preference is optional
    }
  };
  const changePage = (page, pageSize) =>
    setPagination((value) => ({
      ...value,
      current: pageSize === value.pageSize ? page : 1,
      pageSize,
    }));
  useEffect(() => {
    if (productId) loadProduct();
    else {
      setProduct(null);
      loadProducts();
    }
  }, [productId, loadProduct, loadProducts]);
  const refresh = () => (productId ? loadProduct() : loadProducts());
  const openForm = (kind, item) => {
    setModal({ kind, item });
  };
  const openProduct = (item) => navigate(`/optimization/specstudio/${item.id}`);
  const backToList = () => navigate("/optimization/specstudio");
  const editListedProduct = async (item) => {
    setActionLoadingId(item.id);
    try {
      const fullProduct = await service.getProductById(item.id);
      openForm("product", fullProduct);
    } catch (e) {
      message.error(e.response?.data?.message || e.message);
    } finally {
      setActionLoadingId(null);
    }
  };
  const save = async (submitted) => {
    try {
      const v = submitted;
      const { kind, item } = modal;
      if (kind === "product")
        item
          ? await service.updateProduct(item.id, v)
          : await service.createProduct(v);
      if (kind === "provider")
        item
          ? await service.updateProvider(item.id, v)
          : await service.createProvider(product.id, v);
      if (kind === "spec")
        item
          ? await service.updateSpec(item.id, v)
          : await service.createSpec(activeProvider, v);
      message.success("Đã lưu");
      setModal(null);
      refresh();
      loadCategories();
    } catch (e) {
      if (!e.errorFields) message.error(e.response?.data?.message || e.message);
    }
  };
  const copy = async (text, label = "Đã sao chép") => {
    await navigator.clipboard.writeText(text);
    message.success(label);
  };
  const duplicateProduct = async (source = product, openCopy = true) => {
    try {
      const p = await service.duplicateProduct(source.id);
      message.success("Đã nhân bản sản phẩm");
      if (openCopy) navigate(`/optimization/specstudio/${p.id}`);
      else loadProducts();
    } catch (e) {
      message.error(e.response?.data?.message || e.message);
    }
  };
  const cloneProvider = async (p) => {
    try {
      const r = await service.duplicateProvider(p.id, {
        factoryName: `${p.factoryName} (Copy)`,
      });
      message.success("Đã nhân bản nhà in");
      await loadProduct();
      setActiveProvider(r.id);
    } catch (e) {
      message.error(e.response?.data?.message || e.message);
    }
  };
  const remove = async (kind, id) => {
    try {
      if (kind === "product") await service.deleteProduct(id);
      if (kind === "provider") await service.deleteProvider(id);
      if (kind === "spec") await service.deleteSpec(id);
      message.success("Đã xóa");
      if (kind === "product" && productId) navigate("/optimization/specstudio");
      else refresh();
    } catch (e) {
      message.error(e.response?.data?.message || e.message);
    }
  };
  const selectedProvider =
    product?.providers?.find((p) => p.id === activeProvider) ||
    product?.providers?.[0];
  const listPagination = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: pagination.total,
    showSizeChanger: true,
    pageSizeOptions: [24, 48, 96],
    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} sản phẩm`,
  };
  const emptyList = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={query ? "Không tìm thấy sản phẩm" : "Chưa có sản phẩm"}
    />
  );
  const renderProviderPanel = (p) => (
    <>
      <Flex justify="space-between" align="flex-start" gap={12} wrap>
        <Descriptions
          size="small"
          column={1}
          className="specstudio-provider-info"
          items={[
            {
              key: "colorProfile",
              label: "Hệ màu",
              children: p.colorProfile || "—",
            },
            ...(p.notes
              ? [
                  {
                    key: "notes",
                    label: "Lưu ý riêng của nhà in",
                    children: (
                      <Text style={{ whiteSpace: "pre-wrap" }}>{p.notes}</Text>
                    ),
                  },
                ]
              : []),
          ]}
        />
        <Space wrap size={4}>
          {perms.create && (
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => cloneProvider(p)}
            >
              Duplicate nhà in
            </Button>
          )}
          {perms.update && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openForm("provider", p)}
            >
              Sửa nhà in
            </Button>
          )}
          {perms.delete && (
            <Popconfirm
              title={`Xóa nhà in ${p.factoryName}?`}
              description="Toàn bộ vùng in của nhà in này cũng bị xóa."
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove("provider", p.id)}
            >
              <Button danger type="text" icon={<DeleteOutlined />}>
                Xóa
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Flex>
      <Flex
        justify="space-between"
        align="center"
        gap={12}
        className="specstudio-spec-heading-row"
      >
        <Title level={5} style={{ margin: 0 }}>
          Quy chuẩn vùng in
        </Title>
        {perms.create && (
          <Button icon={<PlusOutlined />} onClick={() => openForm("spec")}>
            Thêm vùng in
          </Button>
        )}
      </Flex>
      <RequirementTable
        specs={p.specs || []}
        canEdit={perms.update}
        canDelete={perms.delete}
        onEdit={(r) => openForm("spec", r)}
        onDelete={(id) => remove("spec", id)}
      />
    </>
  );
  return (
    <div className="specstudio-page">
      <div className="specstudio-toolbar">
        <Flex align="center" gap={16}>
          {productId && (
            <Button icon={<ArrowLeftOutlined />} onClick={backToList}>
              Thư viện
            </Button>
          )}
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {productId ? "Quy cách sản phẩm" : "Thư viện quy cách"}
            </Title>
            {!productId && (
              <Text type="secondary">
                Chọn sản phẩm, chọn nhà in. Mọi thông số và template ở cùng một
                nơi.
              </Text>
            )}
          </div>
        </Flex>
        <Space wrap>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={() => setSkuOpen(true)}
          >
            Generate SKU
          </Button>
          {!product && perms.create && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openForm("product")}
            >
              Thêm sản phẩm
            </Button>
          )}
        </Space>
      </div>
      {/* Only the detail view swaps to a spinner; the list keeps its filters
          mounted so the search box keeps focus while results reload */}
      {productId && (loading || !product) ? (
        <Card className="specstudio-panel">
          <Spin />
        </Card>
      ) : productId ? (
        <>
          <Card className="specstudio-panel" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="flex-start" gap={20} wrap>
              <Flex
                gap={16}
                align="flex-start"
                className="specstudio-detail-main"
              >
                {product.thumbnail && (
                  <Image
                    src={product.thumbnail}
                    width={96}
                    height={96}
                    style={{ objectFit: "cover", borderRadius: 8 }}
                    preview={{
                      mask: (
                        <Space size={4}>
                          <EyeOutlined />
                          Xem ảnh
                        </Space>
                      ),
                    }}
                  />
                )}
                <div>
                  <Title level={3} style={{ margin: 0 }}>
                    <Tag color="blue" className="specstudio-sku">
                      {product.productCode}
                    </Tag>
                    {product.name}
                  </Title>
                  <Space size={8} wrap style={{ marginTop: 10 }}>
                    {product.category ? (
                      <Tag color="geekblue" bordered={false}>
                        {product.category.name}
                      </Tag>
                    ) : (
                      <Text type="secondary">Chưa phân loại</Text>
                    )}
                    {product.status === "archived" && <Tag>Đã lưu trữ</Tag>}
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        copy(product.productCode, "Đã sao chép Base SKU")
                      }
                    >
                      Sao chép Base SKU
                    </Button>
                  </Space>
                </div>
              </Flex>
              <Space wrap>
                {perms.update && (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => openForm("product", product)}
                  >
                    Sửa
                  </Button>
                )}
                {perms.create && (
                  <Popconfirm
                    title={`Nhân bản sản phẩm ${product.name}?`}
                    description={`Bản sao gồm ${product.providers?.length || 0} nhà in và toàn bộ vùng in.`}
                    okText="Nhân bản"
                    cancelText="Hủy"
                    onConfirm={() => duplicateProduct()}
                  >
                    <Button icon={<CopyOutlined />}>Duplicate</Button>
                  </Popconfirm>
                )}
                {perms.delete && (
                  <Popconfirm
                    title={`Xóa sản phẩm ${product.name}?`}
                    description="Thao tác này sẽ xóa toàn bộ nhà in và vùng in liên quan."
                    okText="Xóa"
                    cancelText="Hủy"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => remove("product", product.id)}
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      Xóa
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </Flex>
            {product.generalNotes && (
              <>
                <Divider style={{ margin: "16px 0 12px" }} />
                <Descriptions
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "generalNotes",
                      label: "Ghi chú chung",
                      children: (
                        <Text style={{ whiteSpace: "pre-wrap" }}>
                          {product.generalNotes}
                        </Text>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </Card>
          <Card
            className="specstudio-panel"
            title="Nhà in"
            extra={
              perms.create && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openForm("provider")}
                >
                  Thêm nhà in
                </Button>
              )
            }
          >
            {product.providers?.length ? (
              <Tabs
                className="specstudio-provider-tabs"
                activeKey={String(selectedProvider?.id)}
                onChange={(k) => setActiveProvider(Number(k))}
                items={product.providers.map((p) => ({
                  key: String(p.id),
                  label: (
                    <Space size={6}>
                      <ShopOutlined />
                      {p.factoryName}
                      <Badge
                        count={p.specs?.length || 0}
                        showZero
                        size="small"
                        color={
                          p.id === selectedProvider?.id ? "#1890ff" : "#bfbfbf"
                        }
                      />
                    </Space>
                  ),
                  children: renderProviderPanel(p),
                }))}
              />
            ) : (
              <Empty description="Chưa có nhà in" />
            )}
          </Card>
        </>
      ) : (
        <>
          <div className="specstudio-library-filters">
            <Input
              allowClear
              size="large"
              placeholder="Nhập tên sản phẩm hoặc Base SKU (VD: TS2D, Baseball) rồi nhấn Enter để tìm"
              prefix={<SearchOutlined />}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                if (!e.target.value) applySearch("");
              }}
              onPressEnter={() => applySearch(q)}
            />
            <Select
              value={status}
              onChange={(value) => {
                setStatus(value);
                setPagination((current) => ({ ...current, current: 1 }));
              }}
              options={[
                { value: "", label: "Tất cả trạng thái" },
                { value: "active", label: "Đang hoạt động" },
                { value: "archived", label: "Đã lưu trữ" },
              ]}
            />
          </div>
          <div className="specstudio-category-tabs-row">
            <div className="specstudio-category-chips">
              {[
                { id: null, name: "Tất cả sản phẩm" },
                // Only Amazon categories that already have SpecStudio products
                ...categories.filter(
                  (c) => c.productCount > 0 || c.id === categoryId,
                ),
              ].map((c) => (
                <Tag.CheckableTag
                  key={c.id || "all"}
                  checked={categoryId === c.id}
                  onChange={() => {
                    setCategoryId(c.id);
                    setPagination((current) => ({ ...current, current: 1 }));
                  }}
                >
                  {c.name}
                </Tag.CheckableTag>
              ))}
            </div>
            <Space size={12}>
              <Text className="specstudio-product-total">
                {pagination.total} sản phẩm
              </Text>
              <Segmented
                value={viewMode}
                onChange={changeViewMode}
                options={[
                  { value: "grid", icon: <AppstoreOutlined />, label: "Thẻ" },
                  {
                    value: "table",
                    icon: <UnorderedListOutlined />,
                    label: "Bảng",
                  },
                ]}
              />
            </Space>
          </div>
          {viewMode === "grid" ? (
            <List
              className="specstudio-product-grid"
              loading={loading}
              grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 5 }}
              dataSource={products}
              locale={{ emptyText: emptyList }}
              pagination={
                pagination.total > 0 && {
                  ...listPagination,
                  onChange: changePage,
                }
              }
              renderItem={(item) => (
                <List.Item>
                  <ProductCard
                    product={item}
                    onClick={() => openProduct(item)}
                  />
                </List.Item>
              )}
            />
          ) : (
            <Table
              className="specstudio-table specstudio-product-table"
              loading={loading}
              rowKey="id"
              dataSource={products}
              locale={{ emptyText: emptyList }}
              columns={[
                {
                  title: <PictureOutlined />,
                  dataIndex: "thumbnail",
                  key: "thumbnail",
                  width: 72,
                  align: "center",
                  render: (src, item) =>
                    src ? (
                      <Image
                        src={src}
                        alt={item.name}
                        width={40}
                        height={40}
                        style={{ objectFit: "cover", borderRadius: 6 }}
                        preview={{ mask: <EyeOutlined /> }}
                      />
                    ) : (
                      <Avatar
                        shape="square"
                        size={40}
                        icon={<PictureOutlined />}
                      />
                    ),
                },
                {
                  title: "Sản phẩm",
                  dataIndex: "name",
                  key: "name",
                  render: (name, item) => (
                    <Button
                      type="link"
                      className="specstudio-product-name-link"
                      onClick={() => openProduct(item)}
                    >
                      {name}
                    </Button>
                  ),
                },
                {
                  title: "Base SKU",
                  dataIndex: "productCode",
                  key: "productCode",
                  width: 145,
                  render: (code) => (
                    <span className="specstudio-product-code">{code}</span>
                  ),
                },
                {
                  title: "Danh mục",
                  dataIndex: ["category", "name"],
                  key: "category",
                  render: (name) => name || <Text type="secondary">—</Text>,
                },
                {
                  title: "Nhà in",
                  dataIndex: "providers",
                  key: "providers",
                  render: (providers = []) => (
                    <Space
                      size={[4, 4]}
                      wrap
                      className="specstudio-provider-names"
                    >
                      {providers.length ? (
                        providers.map((provider) => (
                          <Tag key={provider.id} icon={<ShopOutlined />}>
                            {provider.factoryName}
                          </Tag>
                        ))
                      ) : (
                        <Text type="secondary">—</Text>
                      )}
                    </Space>
                  ),
                },
                {
                  title: "Trạng thái",
                  dataIndex: "status",
                  key: "status",
                  width: 135,
                  render: (value) => (
                    <Tag color={value === "active" ? "green" : "default"}>
                      {value === "active" ? "Đang hoạt động" : "Đã lưu trữ"}
                    </Tag>
                  ),
                },
                {
                  title: "Thao tác",
                  key: "actions",
                  width: 260,
                  render: (_, item) => (
                    <Space size={4} className="specstudio-product-actions">
                      {perms.update && (
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          loading={actionLoadingId === item.id}
                          onClick={() => editListedProduct(item)}
                        >
                          Sửa
                        </Button>
                      )}
                      {perms.create && (
                        <Popconfirm
                          title={`Nhân bản sản phẩm ${item.name}?`}
                          description="Bản sao sẽ bao gồm các nhà in và thông số hiện có."
                          okText="Nhân bản"
                          cancelText="Hủy"
                          onConfirm={() => duplicateProduct(item, false)}
                        >
                          <Button type="text" icon={<CopyOutlined />}>
                            Duplicate
                          </Button>
                        </Popconfirm>
                      )}
                      {perms.delete && (
                        <Popconfirm
                          title={`Xóa sản phẩm ${item.name}?`}
                          description="Thao tác này sẽ xóa toàn bộ nhà in và thông số liên quan."
                          okText="Xóa"
                          cancelText="Hủy"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => remove("product", item.id)}
                        >
                          <Button danger type="text" icon={<DeleteOutlined />}>
                            Xóa
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                },
              ]}
              pagination={listPagination}
              onChange={(next) => changePage(next.current, next.pageSize)}
              scroll={{ x: 1060 }}
            />
          )}
        </>
      )}
      <SkuGeneratorModal
        open={skuOpen}
        onCancel={() => setSkuOpen(false)}
        products={products.length ? products : product ? [product] : []}
        productId={product?.id}
      />
      <ProductFormModal
        open={modal?.kind === "product"}
        product={modal?.item}
        categories={categories}
        canCreate={perms.create}
        canDelete={perms.delete}
        onCancel={() => setModal(null)}
        onSave={save}
      />
      <ProviderFormModal
        open={modal?.kind === "provider"}
        provider={modal?.item}
        onCancel={() => setModal(null)}
        onSave={save}
      />
      <SpecFormModal
        open={modal?.kind === "spec"}
        spec={modal?.item}
        onCancel={() => setModal(null)}
        onSave={save}
      />
    </div>
  );
}
